/**
 * Menu engineering — screen 07. See docs/architecture.md §5 (access pattern
 * #9) and the prototype's `// ---- menu engineering` block.
 *
 * Everything here is pure and lives in core rather than the component for the
 * reason rule 4 of the agent brief gives: the API and the SPA must agree to
 * the cent. The component's job is to render what these functions return —
 * there is no arithmetic on the other side of this file.
 *
 * Three layers, each usable on its own:
 *
 *   1. `analyseMenu` — costs a menu against its sales and puts every drink in
 *      one of four quadrants: does it sell, and does it make the venue's
 *      gross profit target.
 *   2. `menuScatter` — the prototype's plot: pixel positions, quadrant
 *      dividers, axis ticks, and labels placed so they do not collide.
 *   3. `pricePreview` — what a test price would do, and where the point
 *      would travel to, which is the prototype's dashed ghost.
 */
import { grossProfit } from "./calc.js";
import { specTotals } from "./library.js";
/**
 * Australian GST. Menu prices are inclusive; margin is a profit figure and so
 * is taken ex-GST, exactly as `grossProfit` does. calc.ts keeps its copy
 * private, so `exGstAgreesWithGrossProfit` in the tests pins the two together.
 */
const GST_MULTIPLIER = 1.1;
/** A GST-inclusive menu price as the venue actually banks it. */
export function priceExGst(priceIncGst) {
    return priceIncGst / GST_MULTIPLIER;
}
export const MENU_QUADRANTS = [
    "star",
    "plough-horse",
    "puzzle",
    "dog",
];
export const QUADRANT_LABEL = {
    star: "Signature",
    "plough-horse": "Plough-horse",
    puzzle: "Enigma",
    dog: "Underperformers",
};
/** The prototype's corner captions — what to actually do about each. */
export const QUADRANT_ACTION = {
    star: "Keep",
    "plough-horse": "Reprice",
    puzzle: "Promote",
    dog: "Cut",
};
/**
 * The gross profit a drink is expected to make, percent, until the venue sets
 * its own. The prototype's target line: `Target 74.0%`.
 */
export const DEFAULT_GP_TARGET = 74;
export function isGpTarget(value) {
    return typeof value === "number" && Number.isFinite(value) && value >= 1 && value <= 99;
}
/** The venue's gross profit target, from its META. */
export function gpTargetOf(meta) {
    return isGpTarget(meta?.gpTarget) ? meta.gpTarget : DEFAULT_GP_TARGET;
}
/** A drink is popular at this fraction of an equal share of the menu's units. */
export const POPULAR_SHARE = 0.7;
/** The units a week a drink has to sell to count as popular. */
export function popularUnits(totalUnits, itemCount) {
    return itemCount === 0 ? 0 : (POPULAR_SHARE * totalUnits) / itemCount;
}
export function quadrantOf(unitsPerWeek, gp, popularFrom, gpTarget) {
    // A drink that sold nothing is never popular, even on a menu that sold nothing.
    const popular = unitsPerWeek > 0 && unitsPerWeek >= popularFrom;
    const profitable = gp >= gpTarget;
    if (popular)
        return profitable ? "star" : "plough-horse";
    return profitable ? "puzzle" : "dog";
}
/**
 * What a serve falls short of the target by, dollars: the margin the target
 * asks of this price, less the margin it makes. Zero at or over the target.
 */
export function shortfallPerServe(priceIncGst, cost, gpTarget) {
    const net = priceExGst(priceIncGst);
    return Math.max(0, (gpTarget / 100) * net - (net - cost));
}
/** A recipe is on the menu when the org lists it and the venue has not 86'd it. */
export function isOnMenu(spec, override) {
    if (spec.status !== "available")
        return false;
    return override?.available !== false;
}
/**
 * Cost every drink on the menu, put it in a quadrant, and report the lines
 * the quadrants were drawn against.
 *
 * A drink with no sales row is included at zero units rather than dropped —
 * a new drink nobody has ordered is exactly the thing the manager wants to
 * see in the bottom-left — but it is flagged `estimated` so the screen can
 * say so rather than implying the till reported a zero.
 */
export function analyseMenu(input) {
    const basis = input.basis ?? "margin";
    const gpTarget = input.gpTarget ?? DEFAULT_GP_TARGET;
    const unpricedSpecIds = [];
    const priced = input.specs
        .filter((spec) => isOnMenu(spec, input.overridesBySpecId.get(spec.specId)))
        .flatMap((spec) => {
        const price = input.overridesBySpecId.get(spec.specId)?.price ?? null;
        if (price === null || price <= 0) {
            unpricedSpecIds.push(spec.specId);
            return [];
        }
        // The lines are what is served (#180): a batch is costed as the
        // ingredient it is poured as, from its own build.
        const totals = specTotals(spec, input.ingredientsById);
        const sale = input.salesBySpecId.get(spec.specId);
        const unitsPerWeek = Math.max(0, sale?.unitsPerWeek ?? 0);
        const net = priceExGst(price);
        const contributionMargin = net - totals.cost;
        const gp = grossProfit(price, totals.cost);
        return [
            {
                spec,
                price,
                cost: totals.cost,
                gp,
                pourCost: net === 0 ? 0 : (totals.cost / net) * 100,
                contributionMargin,
                unitsPerWeek,
                weeklyMargin: contributionMargin * unitsPerWeek,
                weeklyRevenue: price * unitsPerWeek,
                unknownIngredientIds: totals.unknownIngredientIds,
                estimated: sale === undefined,
            },
        ];
    });
    const mean = (pick) => priced.length === 0 ? 0 : priced.reduce((total, r) => total + pick(r), 0) / priced.length;
    const meanGp = mean((r) => r.gp);
    const meanMargin = mean((r) => r.contributionMargin);
    const totalUnits = priced.reduce((t, r) => t + r.unitsPerWeek, 0);
    const totalWeeklyMargin = priced.reduce((t, r) => t + r.weeklyMargin, 0);
    const totalWeeklyRevenue = priced.reduce((t, r) => t + r.weeklyRevenue, 0);
    const popularFrom = popularUnits(totalUnits, priced.length);
    const counts = { star: 0, "plough-horse": 0, puzzle: 0, dog: 0 };
    const items = priced.map((r) => {
        const value = basis === "gp" ? r.gp : r.contributionMargin;
        const quadrant = quadrantOf(r.unitsPerWeek, r.gp, popularFrom, gpTarget);
        counts[quadrant] += 1;
        return {
            specId: r.spec.specId,
            name: r.spec.name,
            style: r.spec.style,
            price: r.price,
            cost: r.cost,
            gp: r.gp,
            pourCost: r.pourCost,
            contributionMargin: r.contributionMargin,
            unitsPerWeek: r.unitsPerWeek,
            weeklyMargin: r.weeklyMargin,
            weeklyRevenue: r.weeklyRevenue,
            share: totalWeeklyMargin > 0 ? r.weeklyMargin / totalWeeklyMargin : 0,
            belowTarget: r.gp < gpTarget,
            weeklyShortfall: shortfallPerServe(r.price, r.cost, gpTarget) * r.unitsPerWeek,
            value,
            quadrant,
            unknownIngredientIds: r.unknownIngredientIds,
            estimated: r.estimated,
        };
    });
    return {
        basis,
        items,
        popularUnits: popularFrom,
        gpTarget,
        meanGp,
        meanMargin,
        weightedGp: totalWeeklyRevenue > 0 ? (totalWeeklyMargin / priceExGst(totalWeeklyRevenue)) * 100 : 0,
        totalUnits,
        totalWeeklyMargin,
        totalWeeklyRevenue,
        totalWeeklyShortfall: items.reduce((t, i) => t + i.weeklyShortfall, 0),
        unsoldCount: items.filter((i) => i.unitsPerWeek === 0).length,
        counts,
        unpricedSpecIds,
    };
}
export const SERVICE_PICKS = ["star", "puzzle"];
/**
 * Every Signature and Enigma on the menu, by spec.
 *
 * Empty when no drink has a sales figure. With nothing recorded the quadrants
 * say nothing about what sells, and offering them would recommend drinks on
 * price alone.
 */
export function servicePicks(analysis) {
    const picks = new Map();
    if (analysis.items.every((i) => i.estimated))
        return picks;
    for (const item of analysis.items) {
        if (item.quadrant === "star" || item.quadrant === "puzzle") {
            picks.set(item.specId, { quadrant: item.quadrant, unitsPerWeek: item.unitsPerWeek });
        }
    }
    return picks;
}
// ------------------------------------------------------------------ scales
/** A rounded axis step: 1, 2, 5 or 10 times a power of ten. */
export function tickStep(span, target = 5) {
    if (!(span > 0))
        return 1;
    const raw = span / target;
    const magnitude = 10 ** Math.floor(Math.log10(raw));
    const n = raw / magnitude;
    return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * magnitude;
}
/**
 * A domain that contains every value, padded a little and snapped out to the
 * tick step, so the axis reads in round numbers and no point sits on the frame.
 */
export function niceDomain(values, options = {}) {
    const fallback = options.fallback ?? [0, 1];
    if (values.length === 0)
        return fallback;
    let lo = Math.min(...values);
    let hi = Math.max(...values);
    if (options.fromZero)
        lo = Math.min(0, lo);
    if (hi - lo < 1e-9) {
        lo -= Math.max(1, Math.abs(lo) * 0.1);
        hi += Math.max(1, Math.abs(hi) * 0.1);
    }
    const pad = (hi - lo) * 0.04;
    // Snap on a step finer than the one the axis will label with. Snapping on
    // the label step overshoots badly at the top of the 1/2/5/10 ladder — a
    // best seller at 96 a week would round the axis out to 150 and squash the
    // whole menu into the left two thirds of the plot.
    const step = tickStep(hi - lo, 10);
    const low = options.fromZero ? Math.min(0, lo) : lo - pad;
    return [Math.floor(low / step) * step, Math.ceil((hi + pad) / step) * step];
}
export function ticksFor(domain, target = 5) {
    const step = tickStep(domain[1] - domain[0], target);
    const out = [];
    // Guard the loop rather than trusting float accumulation to land on `hi`.
    for (let v = Math.ceil(domain[0] / step) * step, i = 0; v <= domain[1] + step / 1e6 && i < 64; v += step, i++) {
        out.push(Math.abs(v) < step / 1e6 ? 0 : v);
    }
    return out;
}
/** The prototype's frame: `<rect x=72 y=20 width=556 height=386>` in a 656×470 svg. */
export const DEFAULT_PLOT = { x0: 72, y0: 20, x1: 628, y1: 406 };
/** The prototype's dot, which the label gaps below were measured from. */
const PLAIN_RADIUS = 4.5;
/** A dot's radius runs between these with the drink's weekly margin. */
const MIN_RADIUS = 4;
const MAX_RADIUS = 13;
/** How much a selected dot grows. */
const SELECTED_GROW = 2.5;
/** How many of the biggest earners are named on the plot. */
export const LABELLED_EARNERS = 10;
/** Label geometry, carried over from the prototype's placement pass. */
const LABEL_HEIGHT = 18;
const LABEL_WIDTH_BOX = 140;
const CHAR_WIDTH = 6.4;
const LABEL_PADDING = 16;
/** How close another point may come to a label box before it is penalised. */
const POINT_PAD = 5;
/** Vertical offsets tried, nearest first. */
const LABEL_OFFSETS = [0, -19, 19, -38, 38, -57, 57, -76, 76, -95, 95];
/** Horizontal gaps from the dot: hard against it, then clear of a fat one. */
const LABEL_GAPS = [11, 26];
/**
 * Place a text label beside every point without the labels colliding.
 *
 * The prototype's algorithm, ported unchanged in behaviour: work down the
 * plot, and for each point try the candidate boxes nearest it first — right
 * of the dot, left of the dot, then progressively further above and below.
 * A candidate is scored on the area it overlaps already-placed labels, a flat
 * 900 for every *other* point it would sit on top of (a label covering a dot
 * is worse than two labels touching), and a small pull towards the point's
 * own height. The first candidate with nothing but that pull against it wins
 * outright; otherwise the cheapest does.
 *
 * Pure and deterministic, so a chart looks the same on every render and can
 * be asserted on in a test.
 */
export function placeLabels(points, plot = DEFAULT_PLOT, 
/** Every dot a label must keep off, when only some of them are labelled. */
obstacles = points) {
    const placed = new Map();
    const boxes = [];
    // Top-down, so the crowded middle is filled by points that have already
    // had their nearest slots taken rather than in arbitrary order.
    const order = [...points].sort((a, b) => a.y - b.y || a.key.localeCompare(b.key));
    for (const point of order) {
        const width = point.text.length * CHAR_WIDTH + LABEL_PADDING;
        const candidates = [];
        // A fat dot pushes its label out by as much as it is fatter than a plain one.
        const clear = Math.max(0, (point.r ?? PLAIN_RADIUS) - PLAIN_RADIUS);
        for (const dy of LABEL_OFFSETS) {
            for (const plain of LABEL_GAPS) {
                const gap = plain + clear;
                const y0 = point.y - LABEL_HEIGHT / 2 + dy;
                candidates.push({ side: "right", x0: point.x + gap, x1: point.x + gap + width, y0, y1: y0 + LABEL_HEIGHT });
                candidates.push({ side: "left", x0: point.x - gap - width, x1: point.x - gap, y0, y1: y0 + LABEL_HEIGHT });
            }
        }
        let best;
        let bestPenalty = Infinity;
        for (const box of candidates) {
            if (box.x0 < plot.x0 + 2 || box.x1 > plot.x1 - 2)
                continue;
            if (box.y0 < plot.y0 + 2 || box.y1 > plot.y1 - 2)
                continue;
            let penalty = 0;
            for (const other of boxes) {
                const ox = Math.min(box.x1, other.x1 + 4) - Math.max(box.x0, other.x0 - 4);
                const oy = Math.min(box.y1, other.y1 + 3) - Math.max(box.y0, other.y0 - 3);
                if (ox > 0 && oy > 0)
                    penalty += ox * oy;
            }
            for (const other of obstacles) {
                if (other.key === point.key)
                    continue;
                if (other.x > box.x0 - POINT_PAD &&
                    other.x < box.x1 + POINT_PAD &&
                    other.y > box.y0 - POINT_PAD &&
                    other.y < box.y1 + POINT_PAD) {
                    penalty += 900;
                }
            }
            const pull = Math.abs(box.y0 + LABEL_HEIGHT / 2 - point.y) * 0.6;
            penalty += pull;
            if (penalty < bestPenalty) {
                bestPenalty = penalty;
                best = box;
            }
            if (penalty <= pull + 0.01)
                break;
        }
        const pick = best ?? candidates[0];
        boxes.push(pick);
        placed.set(point.key, pick);
    }
    return placed;
}
/** A label hard against its own dot: to the right, or the left when that would leave the plot. */
function besideDot(x, y, r, text, plot) {
    const width = text.length * CHAR_WIDTH + LABEL_PADDING;
    const gap = LABEL_GAPS[0] + Math.max(0, r - PLAIN_RADIUS);
    const y0 = Math.min(Math.max(y - LABEL_HEIGHT / 2, plot.y0 + 2), plot.y1 - 2 - LABEL_HEIGHT);
    return x + gap + width > plot.x1 - 2
        ? { side: "left", x0: x - gap - width, x1: x - gap, y0, y1: y0 + LABEL_HEIGHT }
        : { side: "right", x0: x + gap, x1: x + gap + width, y0, y1: y0 + LABEL_HEIGHT };
}
/**
 * Whether a drink is showing, given the categories somebody has switched on:
 * its quadrant has to be on, and a drink that sold nothing needs `unsold` too.
 * The plot and the table both ask this, so they never show different drinks.
 */
export function menuItemShown(item, shown) {
    return shown.quadrants.includes(item.quadrant) && (item.unitsPerWeek > 0 || shown.unsold);
}
/**
 * Lay the analysis out as the prototype's plot.
 *
 * Three things keep it readable on a long list (Rob, 2026-10-12). A drink
 * that sold nothing is left off unless asked for (`unsold`) — it has no
 * popularity to plot. A dot is sized on what the drink makes in a week,
 * by area, so the earners stand out whichever axis is showing. And only the
 * biggest earners and the selected drink are named; the rest show their
 * name while pointed at.
 *
 * Domains are computed from the data rather than hardcoded to the
 * prototype's 0–100 units and 72–94% — a venue whose best seller does 140 a
 * week would otherwise fall off the right-hand edge.
 */
export function menuScatter(analysis, options = {}) {
    const plot = options.plot ?? DEFAULT_PLOT;
    const quadrants = options.quadrants ?? MENU_QUADRANTS;
    const unsold = options.unsold ?? analysis.items.every((i) => i.unitsPerWeek === 0);
    const plotted = analysis.items.filter((i) => menuItemShown(i, { quadrants, unsold }));
    const topMargin = Math.max(0, ...plotted.map((i) => i.weeklyMargin));
    const radiusOf = (item, selected) => (topMargin > 0
        ? MIN_RADIUS + (MAX_RADIUS - MIN_RADIUS) * Math.sqrt(Math.max(0, item.weeklyMargin) / topMargin)
        : PLAIN_RADIUS) + (selected ? SELECTED_GROW : 0);
    const named = new Set([...plotted]
        .sort((a, b) => b.weeklyMargin - a.weeklyMargin || a.name.localeCompare(b.name))
        .slice(0, options.labelled ?? LABELLED_EARNERS)
        .map((i) => i.specId));
    if (options.selectedSpecId)
        named.add(options.selectedSpecId);
    const xDomain = niceDomain(plotted.map((i) => i.unitsPerWeek), { fromZero: true, fallback: [0, 100] });
    const yDomain = niceDomain(
    // The target is on the GP axis whatever the drinks make, so the line it
    // draws is never off the plot.
    [...plotted.map((i) => i.value), ...(analysis.basis === "gp" && plotted.length > 0 ? [analysis.gpTarget] : [])], { fallback: analysis.basis === "gp" ? [70, 95] : [0, 20] });
    const spanX = xDomain[1] - xDomain[0] || 1;
    const spanY = yDomain[1] - yDomain[0] || 1;
    const px = (units) => plot.x0 + ((units - xDomain[0]) / spanX) * (plot.x1 - plot.x0);
    const py = (value) => plot.y1 - ((value - yDomain[0]) / spanY) * (plot.y1 - plot.y0);
    const placeable = plotted.map((item) => ({
        key: item.specId,
        x: px(item.unitsPerWeek),
        y: py(item.value),
        text: item.name,
        r: radiusOf(item, options.selectedSpecId === item.specId),
    }));
    const labels = placeLabels(placeable.filter((p) => named.has(p.key)), plot, placeable);
    const points = plotted.map((item) => {
        const x = px(item.unitsPerWeek);
        const y = py(item.value);
        const selected = options.selectedSpecId === item.specId;
        const r = radiusOf(item, selected);
        const box = labels.get(item.specId) ?? besideDot(x, y, r, item.name, plot);
        return {
            specId: item.specId,
            name: item.name,
            quadrant: item.quadrant,
            unitsPerWeek: item.unitsPerWeek,
            value: item.value,
            x,
            y,
            r,
            selected,
            weeklyMargin: item.weeklyMargin,
            named: named.has(item.specId),
            label: {
                x: box.side === "right" ? box.x0 : box.x1 - LABEL_WIDTH_BOX,
                y: box.y0,
                width: LABEL_WIDTH_BOX,
                anchor: box.side === "right" ? "start" : "end",
            },
        };
    });
    return {
        plot,
        width: options.width ?? plot.x1 + 28,
        height: options.height ?? plot.y1 + 64,
        xDomain,
        yDomain,
        xTicks: ticksFor(xDomain).map((value) => ({ value, at: px(value) })),
        yTicks: ticksFor(yDomain).map((value) => ({ value, at: py(value) })),
        popularX: px(analysis.popularUnits),
        targetY: analysis.basis === "gp" && analysis.gpTarget >= yDomain[0] && analysis.gpTarget <= yDomain[1]
            ? py(analysis.gpTarget)
            : null,
        points,
        unplottedCount: analysis.items.length - plotted.length,
        project: (units, value) => ({ x: px(units), y: py(value) }),
    };
}
/**
 * What a test price would do to one drink, holding volume constant.
 *
 * Holding volume constant is a deliberate simplification and worth saying out
 * loud: raising a price nearly always costs some units, and this does not
 * model that. It answers "what would this be worth if nothing else moved",
 * which is the question the prototype's ± control asks.
 */
export function pricePreview(item, testPrice, analysis) {
    const net = priceExGst(testPrice);
    const contributionMargin = net - item.cost;
    const gp = grossProfit(testPrice, item.cost);
    const weeklyMargin = contributionMargin * item.unitsPerWeek;
    return {
        price: testPrice,
        gp,
        pourCost: net === 0 ? 0 : (item.cost / net) * 100,
        contributionMargin,
        weeklyMargin,
        weeklyMarginDelta: weeklyMargin - item.weeklyMargin,
        weeklyShortfall: shortfallPerServe(testPrice, item.cost, analysis.gpTarget) * item.unitsPerWeek,
        quadrant: quadrantOf(item.unitsPerWeek, gp, analysis.popularUnits, analysis.gpTarget),
        unchanged: Math.abs(testPrice - item.price) < 0.005,
    };
}
//# sourceMappingURL=menu.js.map